<template>
  <div>
    <div class="wz-page-header">
      <h1 class="wz-page-title">Períodos vacacionales</h1>
    </div>

    <wz-table-container>

      <wz-table-toolbar>
        <template #left>
          <div style="width: 190px; flex-shrink: 0">
            <wz-select
              :items="companies"
              :value="filters.companyId"
              defaultOption="Todas las empresas"
              @change="onCompanySelected"
            />
          </div>
          <div style="width: 170px; flex-shrink: 0">
            <wz-select
              :items="areas"
              :value="filters.areaId"
              defaultOption="Todas las áreas"
              @change="onAreaSelected"
            />
          </div>
          <div style="width: 160px; flex-shrink: 0">
            <wz-select
              :items="statusOptions"
              :value="filters.status"
              defaultOption="Todos los estados"
              @change="onStatusSelected"
            />
          </div>
        </template>

        <template #right>
          <wz-tooltip text="Recargar">
            <wz-button variant="outline" @click="loadData()">
              <i class="ri-loop-right-line"></i>
            </wz-button>
          </wz-tooltip>
          <wz-search-bar
            placeholder="Buscar por nombre o documento"
            @search="onSearch"
            @input="search = $event"
          />
          <wz-button variant="primary" @click="downloadReport()" :disabled="downloading" style="white-space: nowrap">
            <i class="ri-file-excel-line mr-1"></i>
            {{ downloading ? 'Descargando...' : 'Descargar Excel' }}
          </wz-button>
        </template>

        <template #filters v-if="filters.minPendingDays">
          <wz-badge variant="primary" pill dismissible @dismiss="clearMinPendingDays">
            Días pendientes &gt; {{ filters.minPendingDays }}
          </wz-badge>
        </template>
      </wz-table-toolbar>

      <wz-table>
        <thead>
          <tr>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'employeeName' }" @click="toggleSort('employeeName')">
              Colaborador <i :class="['wz-th-sort-icon', sortIcon('employeeName')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'companyName' }" @click="toggleSort('companyName')">
              Empresa <i :class="['wz-th-sort-icon', sortIcon('companyName')]"></i>
            </th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'areaName' }" @click="toggleSort('areaName')">
              Área <i :class="['wz-th-sort-icon', sortIcon('areaName')]"></i>
            </th>
            <th>Período</th>
            <th class="text-center">Días ganados</th>
            <th class="text-center">Días gozados</th>
            <th class="text-center wz-th-sortable" :class="{ 'is-sorted': sortKey === 'daysPending' }" @click="toggleSort('daysPending')">
              Días pendientes <i :class="['wz-th-sort-icon', sortIcon('daysPending')]"></i>
            </th>
            <th class="text-center">Estado</th>
            <th class="wz-th-sortable" :class="{ 'is-sorted': sortKey === 'expirationDate' }" @click="toggleSort('expirationDate')">
              Vencimiento <i :class="['wz-th-sort-icon', sortIcon('expirationDate')]"></i>
            </th>
            <th>Opciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="paginatedList.length === 0">
            <td colspan="10" class="text-center text-muted py-4">No se encontraron períodos</td>
          </tr>
          <tr v-for="item in paginatedList" :key="item.id">
            <td>{{ item.employeeName || '-' }}</td>
            <td>{{ item.companyName || '-' }}</td>
            <td>{{ item.areaName || '-' }}</td>
            <td>{{ item.periodo || '-' }}</td>
            <td class="text-center">{{ item.daysEarned }}</td>
            <td class="text-center">{{ item.daysTaken }}</td>
            <td class="text-center"><strong>{{ item.daysPending }}</strong></td>
            <td class="text-center">
              <wz-badge :variant="statusVariant(item.status)" pill>{{ statusLabel(item.status) }}</wz-badge>
            </td>
            <td><span v-local-date="item.expirationDate"></span></td>
            <td style="text-align: center;">
              <wz-dropdown :items="rowActions" placement="left" @click="handleRowAction(item, $event)" />
            </td>
          </tr>
        </tbody>
      </wz-table>

      <wz-table-footer>
        <wz-pagination
          v-if="list.length > 0"
          :total="list.length"
          :current-page="page"
          :per-page="pageSize"
          :per-page-options="[10, 20, 50, 100]"
          @change="onPaginationChange"
        />
        <div v-else class="table-no-results">
          <p class="m-0 px-3">No se encontraron períodos.</p>
        </div>
      </wz-table-footer>

    </wz-table-container>
  </div>
</template>

<script>
import { Alert } from "@wizepe/wizeui"
import { SortMixin } from '@wizepe/wizeui'
import { VacationService, CompanyService, AreaService } from '@/core/services'
import { Constants } from '@/core/utils'

export default {
  name: 'VacationPeriodsList',
  mixins: [SortMixin],
  data() {
    return {
      list:        [],
      companies:   [],
      areas:       [],
      search:      '',
      downloading: false,
      page:        1,
      pageSize:    100,
      filters: {
        companyId:      null,
        areaId:         null,
        status:         null,
        minPendingDays: null,
      },
      rowActions: [{ key: 'schedule', label: 'Programar vacaciones' }],
      // Estados mostrables (excluye GOZADO: la lista es de saldos pendientes)
      statusOptions: [
        Constants.VacationPeriodStatus.VIGENTE,
        Constants.VacationPeriodStatus.POR_VENCER,
        Constants.VacationPeriodStatus.VENCIDO,
        Constants.VacationPeriodStatus.CANCELADO,
      ].map(s => ({ key: s.key, label: s.label })),
    }
  },
  computed: {
    paginatedList() {
      const sorted = this.sortedList(this.list)
      const start = (this.page - 1) * this.pageSize
      return sorted.slice(start, start + this.pageSize)
    },
  },
  methods: {
    statusVariant(status) {
      const found = Object.values(Constants.VacationPeriodStatus).find(s => s.key === status)
      return found ? found.color : 'dark'
    },
    statusLabel(status) {
      const found = Object.values(Constants.VacationPeriodStatus).find(s => s.key === status)
      return found ? found.label : status
    },
    normalizeId(id) {
      return (id === null || id === '' || id === 'null' || id === undefined) ? null : id
    },
    onCompanySelected(id) { this.filters.companyId = this.normalizeId(id); this.loadData() },
    onAreaSelected(id)    { this.filters.areaId    = this.normalizeId(id); this.loadData() },
    onStatusSelected(s)   { this.filters.status    = this.normalizeId(s);  this.loadData() },
    onSearch(val) {
      this.search = val
      this.loadData()
    },
    onPaginationChange({ page, perPage }) {
      this.page = page
      this.pageSize = perPage
    },
    handleRowAction(period, action) {
      if (action.key === 'schedule') this.goToCreateRequest(period)
    },
    goToCreateRequest(period) {
      this.$router.push({
        name: 'vacation-requests-create',
        query: {
          companyId: period.companyId,
          employeeId: period.employeeId,
          vacationPeriodId: period.id,
        },
      })
    },
    clearMinPendingDays() {
      this.filters.minPendingDays = null
      // Quitar el query param para que un refresh no lo reaplique
      const query = { ...this.$route.query }
      delete query.minPendingDays
      this.$router.replace({ query }).catch(() => {})
      this.loadData()
    },
    buildParams() {
      const p = {}
      if (this.filters.companyId)      p.companyId      = this.filters.companyId
      if (this.filters.areaId)         p.areaId         = this.filters.areaId
      if (this.filters.status)         p.status         = this.filters.status
      if (this.filters.minPendingDays) p.minPendingDays = this.filters.minPendingDays
      if (this.search)                 p.search         = this.search
      return p
    },
    async loadData() {
      try {
        this.$store.dispatch('app/loading', true)
        const resp = await VacationService.getPeriodsList(this.buildParams())
        this.list = resp.payload || []
        this.page = 1
      } catch (e) {
        Alert.error(e.errors ? e.errors.message : 'Error al cargar los períodos')
      } finally {
        this.$store.dispatch('app/loading', false)
      }
    },
    async downloadReport() {
      try {
        this.downloading = true
        const resp = await VacationService.downloadPeriodsXlsx(this.buildParams())
        const today = new Date().toISOString().split('T')[0]
        const url  = window.URL.createObjectURL(resp.data)
        const link = document.createElement('a')
        link.href  = url
        link.setAttribute('download', `periodos-vacaciones-${today}.xlsx`)
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        Alert.error('Error al descargar el reporte')
      } finally {
        this.downloading = false
      }
    },
  },
  async created() {
    // Pre-filtrado desde el dashboard (query params)
    if (this.$route.query.status) this.filters.status = this.$route.query.status
    if (this.$route.query.minPendingDays) this.filters.minPendingDays = Number(this.$route.query.minPendingDays)

    try {
      const [companiesResp, areasResp] = await Promise.all([
        CompanyService.listAll(),
        AreaService.listAll(),
      ])
      this.companies = (companiesResp.payload || []).map(c => ({ key: c.id, label: c.businessName || c.name }))
      this.areas     = (areasResp.payload     || []).map(a => ({ key: a.id, label: a.description }))
    } catch (e) {
      console.error(e)
    }
    this.loadData()
  },
}
</script>

<style lang="stylus" scoped></style>
